import { Auth0Client, RedirectLoginOptions } from '@auth0/auth0-spa-js'
import { Env } from 'infra/env'
import isMultiFactorAuthBypassFixEnabled from './MultiFactorAuthBypassFixFeatureToggle'
import isImpersonationMfaGuardExceptionEnabled from './ImpersonationMfaGuardExceptionFeatureToggle'
import { verifyAuth0IdToken } from './verifyAuth0IdToken'
import { action, observable, makeObservable } from 'mobx'
import { jwtDecode } from 'jwt-decode'
import QS from 'qs'
import { interval, merge, Observable, Subject, Subscription } from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  first,
  map,
  switchMap,
} from 'rxjs/operators'
import { PageActivity, Router } from 'types/definitions'
import { fromStream } from 'utils/mobx-util'
import { isNotNil } from 'utils/object-util'
import { filterNonNil, shareLatest } from 'utils/rx-util'
import User from 'iam/models/User'
import { UserDTO } from 'iam/dtos/user'
import { AuthUserDTO } from 'iam/dtos/auth-user'

export interface ReferrerParams {
  [key: string]: unknown
}

export interface SessionInfo {
  token: string
  authId: string
}

export type AuthInitialScreen = 'login' | 'signUp'

const origin = window.location.origin

const AUTH0_SCOPE = 'openid profile email offline_access'

export default class AuthStore {
  @observable referrer = ''

  private auth0: Auth0Client
  private referrerParams: ReferrerParams = {}

  /**
   * The token subject which emits when user authenticates or new token is refreshed
   */
  private sessionSubject = new Subject<SessionInfo | null>()

  /**
   * Subject that emits when logout is initiated (before redirect).
   */
  private _logoutSubject$ = new Subject<void>()

  /**
   * Subscription for disposing.
   */
  private subscription = new Subscription()

  constructor(
    private router: Router,
    private pageActivity$: Observable<PageActivity>,
    private userLogout: () => Promise<void>,
    private getLogoutURL: () => string | undefined,
    private isFederatedLogout: () => boolean,
    private getNativeAuthToken: () => Promise<string | null>,
    private authenticate: (token: string) => Promise<AuthUserDTO>,
    private setUser: (user: UserDTO) => User,
    private getUser: (token: string, publicId: string) => Promise<UserDTO>,
  ) {
    makeObservable(this)
    this.auth0 = new Auth0Client({
      clientId: Env.AUTH0_CLIENT_ID,
      useRefreshTokensFallback: true,
      authorizationParams: {
        audience: Env.AUTH0_AUDIENCE_URL,
        redirect_uri: origin,
        scope: AUTH0_SCOPE,
      },
      domain: Env.AUTH0_DOMAIN,
      useRefreshTokens: true,
      cacheLocation: 'localstorage',
    })
    this.subscription.add(
      this.reload$.subscribe(() => {
        window.location.reload()
      }),
    )
  }

  /**
   * Emits a refreshed session or `null` if we got logged out.
   *
   * @private
   */
  private readonly sessionRefresh$ = this.sessionSubject.pipe(
    first(isNotNil),
    switchMap(() =>
      merge(
        this.pageActivity$.pipe(filter((a) => a === PageActivity.Active)),
        interval(10000),
      ).pipe(
        switchMap(() =>
          this.fetchSessionInfo().catch((err) => {
            console.error('Failed to refresh session in the background:', err)
            return null
          }),
        ),
      ),
    ),
  )

  /**
   * Session stream.
   */
  readonly session$ = merge(this.sessionSubject, this.sessionRefresh$).pipe(
    // We only care about changes.
    distinctUntilChanged((a, b) => a?.token === b?.token),
    // Remember the last value for late subscribers.
    shareLatest(),
  )

  /**
   * Emits when we log out (before redirect).
   */
  readonly logout$ = this._logoutSubject$.asObservable()

  /**
   * Emits when we log in elsewhere (another tab).
   * @private
   */
  private readonly loggedInElsewhere$ = this.session$.pipe(
    first((v) => v === null),
    switchMap(() =>
      this.pageActivity$.pipe(
        filter((a) => a === PageActivity.Active),
        switchMap(async () =>
          this.fetchSessionInfo().catch((err) => {
            logAuthError(err)
            return null
          }),
        ),
        filterNonNil(),
      ),
    ),
  )

  /**
   * Emits when we should reload the page due to either being
   * logged out or detected that we logged in somewhere else (another tab)
   * @private
   */
  private readonly reload$ = merge(this.loggedInElsewhere$, this.logout$)

  /**
   * The token.
   */
  token$ = this.session$.pipe(
    map((s) => s?.token),
    filterNonNil(),
    distinctUntilChanged(),
  )

  /**
   * Getter for the token.
   */
  getToken = () => this.$.token()

  /**
   * RxJS-Observables as MobX-Observables using `fromStream`.
   */
  private $ = {
    token: fromStream(null, this.token$),
  }

  async _showDialogAndAuthorize(
    initialScreen: AuthInitialScreen,
    opts: RedirectLoginOptions | undefined,
  ) {
    this.auth0
      .logout({
        openUrl: false,
      })
      .then()

    return this.auth0.loginWithRedirect({
      ...opts,
      appState: {
        // TODO use appState for redirect URL?
        // ...opts,
        customReferrerParams: this.referrerParams,
      },
      authorizationParams: {
        initial_screen: initialScreen || 'login',
        screen_hint: initialScreen?.toLowerCase() || 'login',
        referrer: document.location.href,
        scope: AUTH0_SCOPE,
        connections: window.__auth0Connections.filter(
          (s) => s !== 'rocket-lawyer-sso',
        ),
      },
    })
  }

  @action
  setReferrerMeta(data: ReferrerParams) {
    this.referrerParams = data
  }

  @action.bound
  async logout(url?: string | null) {
    this._logoutSubject$.next()
    const redirectUrl = url || this.getLogoutURL()
    await this.userLogout()
    this.auth0
      .logout({
        logoutParams: {
          returnTo:
            origin +
            (redirectUrl
              ? `?redirect_to=${encodeURIComponent(redirectUrl)}`
              : ''),
          federated: this.isFederatedLogout(),
        },
      })
      .then()
  }

  /**
   * Called by the router to check if we are still authenticated.
   */
  @action
  async checkAuthentication() {
    // If there is a code + state in the URL then attempt to exchange
    // it for a new token
    const { code, state } = QS.parse(window.location.search, {
      ignoreQueryPrefix: true,
    })

    // Only attempt to run Auth0's OAuth flow if the current
    // path is the root. Otherwise we might be interfering with
    // the Stripe OAuth.
    const isRoot = window.location.pathname === '/'
    if (isRoot && code && state) {
      // Temporary Firefox workaround
      // https://github.com/auth0/auth0-spa-js/blob/master/FAQ.md#why-do-i-get-error-invalid-state-in-firefox-when-refreshing-the-page-immediately-after-a-login
      window.location.hash = window.location.hash // eslint-disable-line no-self-assign

      try {
        await this.auth0.handleRedirectCallback()

        // It was a login redirect, restore the url
        const returnUrl = localStorage.getItem('returnUrl')
        localStorage.removeItem('returnUrl')
        if (returnUrl) {
          this.router.replace(returnUrl)
        }
      } catch (err) {
        console.error('Code exchange failed:', err)
        // Remove query string from URL to remove ?code=...&state=...
        this.router.replace(this.router.location.pathname)
      }
    }

    localStorage.removeItem('returnUrl')

    try {
      // Gets a token from the cache or performs a background refresh
      const session = await this.fetchSessionInfo()
      const authUser = await this.authenticate(session.token)
      const userDTO = await this.getUser(session.token, authUser.public_id)

      const decoded = jwtDecode<any>(session.token)
      const mfaEnrolled = Boolean(
        decoded['https://worklayer.com/claims.mfa_enrolled'],
      )

      const user = this.setUser(userDTO)
      if (mfaEnrolled) {
        user.markMultiFactorAuthEnabled()
      }

      user.markFullyLoaded()

      this.sessionSubject.next(session)
      return true
    } catch (err) {
      logAuthError(err)
      this.sessionSubject.next(null)
      // Save the current URL to local storage so we can redirect after login.
      this.saveCurrentURLToStorage()

      if (err?.response?.data?.message?.includes('already in use')) {
        throw new Error(err.response.data.message)
      }
    }
  }

  private async fetchSessionInfo(): Promise<SessionInfo> {
    // Fetch from localstorage for e2e test only, real implementation
    // doesn't store real tokens in this key..
    const testToken = localStorage.getItem('mockjwt')
    if (testToken) {
      const claims = jwtDecode<{ sub: string; iss: string }>(testToken)
      if (claims.iss === 'https://mock.taxfyle.auth0.com') {
        return {
          token: testToken,
          authId: claims.sub,
        }
      }
    }

    const nativeToken = await this.getNativeAuthToken().catch((err) => {
      console.error('Failed to get native auth token:', err)
      return null
    })
    if (nativeToken) {
      const claims = jwtDecode<{ sub: string }>(nativeToken)
      return {
        token: nativeToken,
        authId: claims.sub,
      }
    }

    if (
      isMultiFactorAuthBypassFixEnabled() &&
      !(await this.auth0.isAuthenticated()) &&
      !(await this.isVerifiedImpersonationLanding())
    ) {
      throw Object.assign(new Error('login_required'), {
        error: 'login_required',
      })
    }

    // Client caches tokens in localstorage and only reaches out to
    // auth0 when token is about to expire
    const token = await this.auth0.getTokenSilently()
    const user = await this.auth0.getUser()
    if (!user?.sub) {
      throw new Error('No user returned from getUser.')
    }

    return { token, authId: user.sub }
  }

  /** Verified impersonation landings are exempt from the MFA-bypass guard. */
  private async isVerifiedImpersonationLanding(): Promise<boolean> {
    if (!isImpersonationMfaGuardExceptionEnabled()) {
      return false
    }
    const idToken = new URLSearchParams(
      window.location.hash.replace(/^#/, ''),
    ).get('id_token')
    if (!idToken) {
      return false
    }
    return verifyAuth0IdToken(idToken)
  }

  dispose = () => this.subscription.unsubscribe()

  private saveCurrentURLToStorage() {
    const pathName = this.router.location.pathname

    // Check for URLs we dont want to redirect user back to
    if (pathName === '/' || pathName === '/logout') {
      localStorage.removeItem('returnUrl')
      return
    }

    const queryString = this.router.location.search
    const url = pathName + queryString
    localStorage.setItem('returnUrl', url)
  }
}

function logAuthError(err: any) {
  if (
    err?.error !== 'login_required' &&
    err?.error !== 'missing_refresh_token'
  ) {
    console.error('Get token silently error:', err)
  }
}
